import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { VideoInsight } from "@/api/studio/insights/types/insight.type";
import { Benchmark } from "@/api/studio/library/types/benchmark.type";
import { getBenchmarkComparison } from "@/api/studio/library/utils/get-benchmark-comparison";
import { InfoModalTrigger } from "@/components/ui/modal";
import { Progress } from "@/components/ui/progress";
import { VideoBenchmarkPill } from "@/features/reports/components/video-benchmark-pill";
import { VideoCopilotSummaryPanel } from "@/features/reports/components/in-depth/video-copilot-summary-panel";
import { VideoPillarRow } from "@/features/reports/components/in-depth/video-pillar-row";
import { VIDEO_METRIC_DEFINITIONS_MODAL_CONFIG, VideoMetricDefinitionsModal } from "@/features/reports/modals/video-metric-definitions-modal";

/* Prop Types */
interface VideoMetricsDetailsProps {
  currentVideo: VideoInsight;
  benchmark?: Benchmark;
}

/**
 * VideoMetricsDetails component
 *
 * Renders the video in-depth right panel: the Creative Impact score as a progress bar + percentage
 * with its benchmark comparison, the applied benchmark, expandable Attention / Emotion / Memory
 * rows, and the video's Copilot summary.
 */
export const VideoMetricsDetails = ({ currentVideo, benchmark }: VideoMetricsDetailsProps) => {
  /* Consts */
  const { creative } = currentVideo;
  const { attention, emotion, memory } = creative;
  const comparison = benchmark ? getBenchmarkComparison(creative.score, benchmark) : undefined;
  // Emotion and memory are null until the pipeline has produced them for the video
  const pillars = [
    {
      label: "Attention",
      score: attention.score,
      qualityScores: [
        { label: "Hook", score: attention.hook.score },
        { label: "Visual ease", score: attention.visualEase.score },
        { label: "Hold", score: attention.hold.score },
      ],
    },
    {
      label: "Emotion",
      score: emotion?.score ?? null,
      qualityScores: emotion
        ? [
            { label: "Intensity", score: emotion.intensity.score },
            { label: "Payoff", score: emotion.payoff.score },
            { label: "Authenticity", score: emotion.authenticity.score },
          ]
        : [],
    },
    {
      label: "Memory",
      score: memory?.score ?? null,
      qualityScores: memory
        ? [
            { label: "Brand attribution", score: memory.brandAttribution.score },
            { label: "Message memory", score: memory.messageMemory.score },
          ]
        : [],
    },
  ];

  /* Render */
  return (
    <div className="flex flex-col gap-4 w-[443px] shrink-0">
      <div className="flex flex-col gap-2">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <h2 className="font-semibold">Creative Impact</h2>
              <InfoModalTrigger modalContent={<VideoMetricDefinitionsModal />} config={VIDEO_METRIC_DEFINITIONS_MODAL_CONFIG} iconClassName="size-4 text-base-content/50" />
            </div>
            <p className="text-sm text-base-content/70 max-w-[236px]">The overall creative score of the video.</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <h2 className="font-semibold">{Math.round(creative.score)}%</h2>
            {comparison && <VideoBenchmarkPill comparison={comparison} />}
          </div>
        </div>
        <div className="relative">
          <Progress value={Math.round(creative.score)} className="w-full h-2" />
          {benchmark && (
            <div
              data-testid="benchmark-range-marker"
              className="absolute -top-[3px] -bottom-[3px] rounded-full border-2 border-dashed border-base-content/40"
              style={{ left: `${benchmark.scoreRangeMin}%`, width: `${benchmark.scoreRangeMax - benchmark.scoreRangeMin}%` }}
            />
          )}
        </div>

        {benchmark && (
          <div className="flex items-center justify-between bg-base-200 rounded-lg p-4">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <p className="font-semibold">{benchmark.name}</p>
                <InformationCircleIcon className="size-4 text-base-content/50" />
              </div>
              <p className="text-sm text-base-content/70">{benchmark.videoCount.toLocaleString()} videos</p>
            </div>
            <p className="font-semibold">
              {benchmark.scoreRangeMin}-{benchmark.scoreRangeMax}%
            </p>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-px rounded-lg overflow-hidden bg-base-100">
        {pillars.map(({ label, score, qualityScores }) => (
          <VideoPillarRow key={label} label={label} score={score} qualityScores={qualityScores} infoModalContent={<VideoMetricDefinitionsModal />} />
        ))}
      </div>

      {currentVideo.summary && <VideoCopilotSummaryPanel summary={currentVideo.summary} />}
    </div>
  );
};
